import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  ACTIVE_VARIANTS,
  CHALLENGE_COLORS,
  GAME_MODES,
  DEFAULT_GAME_MODE,
  DEFAULT_OPENING_MODE,
  openingModesFor,
  resolveOpeningMode,
} from '@draughts/shared';
import type { ChallengeColor, GameMode, OpeningMode } from '@draughts/shared';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { usePreferencesStore } from '../../store/preferences-store';
import { trackVariantSelected, trackAiDifficultySelected } from '../../utils/analytics';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { colorLeading, gameModeLeading, openingLeading } from '../../utils/challenge-icons';
import { colorLabel, gameModeLabel } from '../../utils/challenge-labels';
import { AI_DIFFICULTIES, AI_DIFFICULTY_LABEL_KEY, AI_DIFFICULTY_DOT, type AIDifficulty } from '../../utils/ai-difficulty';
import { openingOptionLabel } from '../../utils/opening-labels';
import {
  getLastVariant, setLastVariant,
  getLastAiDifficulty, setLastAiDifficulty,
  getLastAiOpening, setLastAiOpening,
  getLastAiColor, setLastAiColor,
  getLastAiGameMode, setLastAiGameMode,
} from '../../utils/last-used-play';
import VariantSelect from '../profile/VariantSelect';
import './PlayModals.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

/**
 * Ri-esportato: la scala vive ora in `utils/ai-difficulty`, perché la sceglie anche
 * la riga "vs Computer" della Partita personalizzata. Chi importava il tipo da qui
 * (App) continua a trovarlo.
 */
export type { AIDifficulty };

/**
 * Le tre impostazioni che la Sfida AI condivide con la Sfida diretta. `opening` è già
 * RISOLTA contro la variante scelta (un ballot su una variante senza tabella federale
 * arriva qui come 'random3'), così chi la riceve non deve risolverla di nuovo.
 */
export interface AIGameOptions {
  opening: OpeningMode;
  color: ChallengeColor;
  ruleMode: GameMode;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (variant: string, difficulty: AIDifficulty, options: AIGameOptions) => void;
  defaultVariant?: string;
  defaultDifficulty?: AIDifficulty;
  /**
   * Hide the variant selector and lock the variant to `defaultVariant`. Used when
   * the variant is already fixed by the context (e.g. continuing a solved puzzle
   * vs the AI on that puzzle's board) — only the difficulty is chosen here.
   */
  hideVariant?: boolean;
  /**
   * Nasconde il selettore apertura: non ha senso quando la partita comincia da una
   * posizione già impostata (editor, proseguimento di un puzzle).
   */
  hideOpening?: boolean;
  /** Nasconde il selettore colore (il puzzle continua con il colore del puzzle). */
  hideColor?: boolean;
  /** Nasconde il selettore modalità. */
  hideMode?: boolean;
  /**
   * When set (dashboard only), the variant/difficulty dropdowns are seeded from the
   * device-local "last used" store and every change is persisted there, so reopening
   * the modal restores the last choice. Off for guest/editor/puzzle callers.
   */
  rememberSettings?: boolean;
}

// GameVariant enum values are UPPERCASE (game-store/setVariant expects them).
const VARIANTS: string[] = [...ACTIVE_VARIANTS];

export default function DashboardPlayAIModal({
  isOpen,
  onClose,
  onConfirm,
  defaultVariant = 'INTERNATIONAL',
  defaultDifficulty = 'medium',
  hideVariant = false,
  hideOpening = false,
  hideColor = false,
  hideMode = false,
  rememberSettings = false,
}: Props) {
  const { t } = useTranslation();
  // Float the user's preferred variant to the top of the dropdown (stored
  // lowercase; the dropdown lists the UPPERCASE enum, so normalise the casing).
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);
  // Seed from the "last used" store when persistence is on, else from the passed
  // default. Never read the store for the variant when it's locked (hideVariant),
  // so a puzzle/editor continuation keeps its fixed board variant.
  const seedVariant = (): string => {
    const stored = rememberSettings && !hideVariant ? getLastVariant() : null;
    const base = stored ?? defaultVariant;
    return base ? base.toUpperCase() : 'INTERNATIONAL';
  };
  const seedDifficulty = (): AIDifficulty => {
    const stored = rememberSettings ? getLastAiDifficulty() : null;
    return stored ?? defaultDifficulty;
  };
  // Il colore parte dal Bianco, non dal sorteggio come nella sfida fra umani: contro il
  // computer l'umano ha sempre giocato il Bianco, e chi conferma senza toccare nulla non
  // deve trovarsi dall'altra parte. Il sorteggio resta a un click di distanza.
  const seedColor = (): ChallengeColor => (rememberSettings ? getLastAiColor() ?? 'white' : 'white');
  const seedRuleMode = (): GameMode => (rememberSettings ? getLastAiGameMode() ?? DEFAULT_GAME_MODE : DEFAULT_GAME_MODE);
  const seedOpening = (): OpeningMode => (rememberSettings ? getLastAiOpening() ?? DEFAULT_OPENING_MODE : DEFAULT_OPENING_MODE);

  const [variant, setVariant] = useState(seedVariant);
  const [difficulty, setDifficulty] = useState<AIDifficulty>(seedDifficulty);
  // Scelta GREZZA: `opening` più sotto è questa risolta contro la variante, così passare
  // a una variante senza tabella federale e tornare indietro non perde la preferenza.
  const [rawOpening, setOpening] = useState<OpeningMode>(seedOpening);
  const [color, setColor] = useState<ChallengeColor>(seedColor);
  const [ruleMode, setRuleMode] = useState<GameMode>(seedRuleMode);

  const openingModes = openingModesFor(variant);
  const opening = resolveOpeningMode(rawOpening, variant);

  // Reset selection when reopened.
  useEffect(() => {
    if (isOpen) {
      setVariant(seedVariant());
      setDifficulty(seedDifficulty());
      setOpening(seedOpening());
      setColor(seedColor());
      setRuleMode(seedRuleMode());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultVariant, defaultDifficulty, rememberSettings, hideVariant]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Close on a device back press.
  useOverlayBackButton(isOpen, onClose);

  /*
   * Il fuoco dentro il dialogo, Tab che non ne esce, e alla chiusura il fuoco
   * torna a chi l'aveva. Ruolo, nome ed Escape c'erano già; il fuoco no — e senza
   * di lui la tabulazione continua nella pagina SOTTO, che è ancora lì dietro il
   * velo, su bottoni che nessuno vede.
   *
   * Chiamato PRIMA del `return null` qui sotto — un hook non può stare dietro a
   * una condizione — con `isOpen` come interruttore. E senza `onClose`: l'Escape
   * lo gestisce già il componente, passarlo anche all'hook chiuderebbe due volte.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal dashboard-modal play-online-modal"
        ref={riferimentoDialogo}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={t('dashboard.hero.ctaVsComputer')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="play-online-head">
          <h2>{t('dashboard.hero.ctaVsComputer')}</h2>
          <button type="button" className="play-online-close" aria-label={t('common.cancel')} onClick={onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Options — one dropdown per setting, stacked, matching the Sfida diretta
            (SendChallengeModal) look: labelled row + glass VariantSelect. */}
        <div className="play-online-body">
          {!hideVariant && (
            <div className="modal-field">
              <span className="modal-label">{t('home.variant')}</span>
              <VariantSelect
                value={variant}
                onChange={(v) => { setVariant(v); trackVariantSelected(v.toLowerCase()); if (rememberSettings && !hideVariant) setLastVariant(v); }}
                variants={VARIANTS}
                preferred={preferredVariant ? preferredVariant.toUpperCase() : undefined}
                label={(v) => variantShortLabel(t, v)}
                dotColor={() => 'transparent'}
                renderLeading={(v) => variantPill(v).flag}
                ariaLabel={t('home.variant')}
              />
            </div>
          )}

          <div className="modal-field">
            <span className="modal-label">{t('home.difficulty')}</span>
            <VariantSelect
              value={difficulty}
              onChange={(d) => { setDifficulty(d as AIDifficulty); trackAiDifficultySelected(d as AIDifficulty); if (rememberSettings) setLastAiDifficulty(d as AIDifficulty); }}
              variants={AI_DIFFICULTIES}
              label={(d) => t(`game.modes.${AI_DIFFICULTY_LABEL_KEY[d as AIDifficulty]}`)}
              dotColor={(d) => AI_DIFFICULTY_DOT[d as AIDifficulty]}
              ariaLabel={t('home.difficulty')}
            />
          </div>

          {!hideOpening && (
            <div className="modal-field">
              <span className="modal-label">{t('online.challenge.opening')}</span>
              <VariantSelect
                value={opening}
                onChange={(v) => { setOpening(v as OpeningMode); if (rememberSettings) setLastAiOpening(v as OpeningMode); }}
                variants={openingModes}
                label={(o) => openingOptionLabel(t, o as OpeningMode, variant)}
                dotColor={() => 'transparent'}
                renderLeading={(o) => openingLeading(o as OpeningMode)}
                ariaLabel={t('online.challenge.opening')}
              />
            </div>
          )}

          {!hideColor && (
            <div className="modal-field">
              <span className="modal-label">{t('online.challenge.color')}</span>
              <VariantSelect
                value={color}
                onChange={(v) => { setColor(v as ChallengeColor); if (rememberSettings) setLastAiColor(v as ChallengeColor); }}
                variants={CHALLENGE_COLORS}
                label={(c) => colorLabel(t, c)}
                dotColor={() => 'transparent'}
                renderLeading={colorLeading}
                ariaLabel={t('online.challenge.color')}
              />
            </div>
          )}

          {!hideMode && (
            <div className="modal-field">
              <span className="modal-label">{t('online.challenge.mode')}</span>
              <VariantSelect
                value={ruleMode}
                onChange={(v) => { setRuleMode(v as GameMode); if (rememberSettings) setLastAiGameMode(v as GameMode); }}
                variants={GAME_MODES}
                label={(m) => gameModeLabel(t, m)}
                dotColor={() => 'transparent'}
                renderLeading={gameModeLeading}
                ariaLabel={t('online.challenge.mode')}
              />
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="primary"
            // Si emette l'apertura RISOLTA (quella che si giocherà davvero), mentre
            // quella grezza resta memorizzata: stessa scelta di SendChallengeModal.
            onClick={() => onConfirm(variant, difficulty, { opening, color, ruleMode })}
          >
            {t('dashboard.hero.ctaVsComputer')}
          </button>
        </div>
      </div>
    </div>
  );
}
